import React from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { Link as RouterLink, Navigate, useParams } from "react-router";
import Hero from "src/Components/Hero";
import SeoHead from "src/Components/SeoHead";
import ContactUs from "src/Components/Modals/ContactUs";
import { useLocale } from "src/i18n/LocaleProvider";
import {
  getMarketCity,
  getMarketCountry,
  localizedMarketList,
  localizedMarketText,
  MARKET_COUNTRIES,
} from "src/i18n/marketsContent";
import {
  audiencePath,
  homePath,
  marketsPath,
  pricingPath,
} from "src/i18n/paths";
import {
  breadcrumbSchema,
  organizationSchema,
  serviceSchema,
  webPageSchema,
} from "src/utils/seoSchema";

const sectionSx = {
  width: "100%",
  bgcolor: "#0b0f17",
  py: { xs: 6, md: 8 },
  px: { xs: 2, sm: 3, lg: 4 },
};

const innerSx = {
  maxWidth: 960,
  mx: "auto",
  width: "100%",
};

export const CountryHubPage: React.FC = () => {
  const { country: countrySlug } = useParams<{ country: string }>();
  const { locale, config } = useLocale();
  const country = countrySlug ? getMarketCountry(countrySlug) : undefined;

  if (!country) {
    return <Navigate to={homePath(locale)} replace />;
  }

  const title = localizedMarketText(country.seoTitle, locale);
  const description = localizedMarketText(country.seoDescription, locale);
  const keywords = localizedMarketText(country.keywords, locale);
  const heroTitle = localizedMarketText(country.heroTitle, locale);
  const heroSubtitle = localizedMarketText(country.heroSubtitle, locale);
  const regulation = localizedMarketText(country.regulationBlurb, locale);
  const name = localizedMarketText(country.name, locale);
  const path = marketsPath(country.slug, locale);

  return (
    <>
      <SeoHead
        title={title}
        description={description}
        keywords={keywords}
        canonical={path}
        jsonLd={[
          organizationSchema(),
          webPageSchema({ name: title, description, url: path }),
          serviceSchema({
            name: title,
            description,
            audience: name,
            url: path,
            areaServedCountry: config.countryName,
          }),
          breadcrumbSchema([
            { name: "Home", url: homePath(locale) },
            { name, url: path },
          ]),
        ]}
      />
      <Hero title={heroTitle} subTitle={heroSubtitle} showRadar />
      <Box component="section" sx={sectionSx}>
        <Box sx={innerSx}>
          <Typography
            variant="h2"
            sx={{ color: "#fff", fontWeight: 700, fontSize: "1.75rem", mb: 2 }}
          >
            {name}
          </Typography>
          <Typography sx={{ color: "#94a3b8", mb: 4, lineHeight: 1.7 }}>
            {regulation}
          </Typography>
          <Typography
            variant="h3"
            sx={{ color: "#d8b4fe", fontWeight: 600, fontSize: "1.25rem", mb: 2 }}
          >
            Priority cities
          </Typography>
          <Stack spacing={2} mb={4}>
            {country.cities.map((city) => (
              <Button
                key={city.slug}
                component={RouterLink}
                to={marketsPath(country.slug, locale, city.slug)}
                variant="outlined"
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  borderColor: "rgba(139, 92, 246, 0.45)",
                  color: "#fff",
                  py: 1.5,
                }}
              >
                {city.name}
              </Button>
            ))}
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              component={RouterLink}
              to={audiencePath("cities", locale)}
              variant="contained"
              sx={{ textTransform: "none" }}
            >
              Audience solutions
            </Button>
            <Button
              component={RouterLink}
              to={pricingPath(locale)}
              variant="outlined"
              sx={{ textTransform: "none", color: "#fff" }}
            >
              Pricing
            </Button>
            <ContactUs analyticsContext={{ source: `market_${country.slug}` }} />
          </Stack>
        </Box>
      </Box>
    </>
  );
};

export const CitySeoPage: React.FC = () => {
  const { country: countrySlug, city: citySlug } = useParams<{
    country: string;
    city: string;
  }>();
  const { locale, config } = useLocale();
  const match =
    countrySlug && citySlug
      ? getMarketCity(countrySlug, citySlug)
      : undefined;

  if (!match) {
    return <Navigate to={homePath(locale)} replace />;
  }

  const { country, city } = match;
  const title = localizedMarketText(city.seoTitle, locale);
  const description = localizedMarketText(city.seoDescription, locale);
  const keywords = localizedMarketText(city.keywords, locale);
  const heroTitle = localizedMarketText(city.heroTitle, locale);
  const heroSubtitle = localizedMarketText(city.heroSubtitle, locale);
  const paragraphs = localizedMarketList(city.body, locale);
  const countryName = localizedMarketText(country.name, locale);
  const path = marketsPath(country.slug, locale, city.slug);

  return (
    <>
      <SeoHead
        title={title}
        description={description}
        keywords={keywords}
        canonical={path}
        jsonLd={[
          organizationSchema(),
          webPageSchema({ name: title, description, url: path }),
          serviceSchema({
            name: title,
            description,
            audience: city.name,
            url: path,
            areaServedCountry: config.countryName,
          }),
          breadcrumbSchema([
            { name: "Home", url: homePath(locale) },
            {
              name: countryName,
              url: marketsPath(country.slug, locale),
            },
            { name: city.name, url: path },
          ]),
        ]}
      />
      <Hero title={heroTitle} subTitle={heroSubtitle} showRadar />
      <Box component="section" sx={sectionSx}>
        <Box sx={innerSx}>
          <List>
            {paragraphs.map((text) => (
              <ListItem key={text.slice(0, 40)} alignItems="flex-start" sx={{ px: 0 }}>
                <ListItemText
                  primary={text}
                  primaryTypographyProps={{
                    sx: { color: "#cbd5e1", lineHeight: 1.75, mb: 1 },
                  }}
                />
              </ListItem>
            ))}
          </List>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={3}>
            <Button
              component={RouterLink}
              to={marketsPath(country.slug, locale)}
              variant="outlined"
              sx={{ textTransform: "none", color: "#fff" }}
            >
              {countryName}
            </Button>
            <Button
              component={RouterLink}
              to={pricingPath(locale)}
              variant="contained"
              sx={{ textTransform: "none" }}
            >
              Get a quote
            </Button>
            <ContactUs
              analyticsContext={{
                source: `market_${country.slug}_${city.slug}`,
              }}
            />
          </Stack>
        </Box>
      </Box>
    </>
  );
};

export const MarketsIndexLinks: React.FC = () => {
  const { locale } = useLocale();
  return (
    <Stack spacing={1}>
      {MARKET_COUNTRIES.map((country) => (
        <Button
          key={country.slug}
          component={RouterLink}
          to={marketsPath(country.slug, locale)}
          variant="text"
          sx={{ justifyContent: "flex-start", color: "#d8b4fe", textTransform: "none" }}
        >
          {localizedMarketText(country.name, locale)}
        </Button>
      ))}
    </Stack>
  );
};
